<script>
import DoughnutChart from '@components/charts/doughnut-chart.vue';
import carousel from 'vue-owl-carousel';

import moment from 'moment';
import _ from 'lodash';
import { orgComputed } from '@state/helpers';

export default {
  components: {
    DoughnutChart,
    carousel,
  },
  props: {
    weeks: {
      type: Array,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      doughnutChartHeight: '0px',
      doughnutChartWidth: '0px',
      chartData: {
        datasets: [
          {
            backgroundColor: [process.env.VUE_APP_ORG_ID === '618183d65e84466f63c38878' ? '#FF6E69' : '#4059E1', 'black'],
            borderWidth: 0,
            hoverBorderColor: false,
          },
        ],
      },
      chartOpts: {
        cutoutPercentage: 90,
        tooltips: {
          enabled: false,
        },
        legend: {
          display: false,
        },
        hover: false,
      },
      weekDays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      currentMonth: moment().format('YYYY-MM'),
      monthOptions: [
        moment(this.currentMonth)
          .add(-5, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(-4, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(-3, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(-2, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(-1, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(0, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(+1, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(+2, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(+3, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(+4, 'months')
          .format('YYYY-MM'),
        moment(this.currentMonth)
          .add(+5, 'months')
          .format('YYYY-MM'),
      ],
    };
  },
  mounted() {
    this.getDoughnutChartDim();
  },
  computed: {
    ...orgComputed,
    currentMonthName() {
      return this.currentMonth && moment(this.currentMonth).format('MMMM yyyy');
    },
    allDaysInWeeks() {
      const daysObj = {};
      if (this.weeks && this.weeks.length > 0) {
        this.weeks.forEach((week) => {
          week.days.forEach((day) => {
            daysObj[moment(day.date).format('YYYY-MM-DD')] = day;
          });
        });
        return daysObj;
      } else {
        return {};
      }
    },
    cssVars() {
      return {
        '--color': this.primaryColor,
      };
    },
  },
  methods: {
    getDoughnutChartDim() {
      const canvas = document.getElementById('doughnut-chart');
      if (canvas) {
        this.doughnutChartHeight = canvas.clientHeight + 'px';
        this.doughnutChartWidth = canvas.clientWidth + 'px';
      }
    },
    isToday(day, dayISODate) {
      if (!dayISODate) {
        day = day.toLowerCase();
        const today = moment()
          .format('dddd')
          .toLowerCase();
        return moment.utc(this.currentMonth).isSame(moment.utc(), 'month') && day === today;
      } else {
        return moment.utc(this.currentMonth).isSame(moment.utc(), 'month') && moment.utc(dayISODate).isSame(moment.utc(), 'day');
      }
    },
    getPastISO(month, step) {
      return moment(month)
        .startOf('month')
        .subtract(step, 'days')
        .toISOString();
    },
    getDaysInMonth(month) {
      const daysArray = Array.from(
        // Here we get the actual day of the selected month
        { length: moment(month).daysInMonth() },
        (x, i) => {
          return {
            dayNum: moment(month)
              .startOf('month')
              .add(i, 'days')
              .format('D'),
            dayName: moment(month)
              .startOf('month')
              .add(i, 'days')
              .format('dddd'),
            dayISODate: moment(month)
              .startOf('month')
              .add(i, 'days')
              .toISOString(),
          };
        }
      );

      const merged = [];
      for (let i = 0; i < daysArray.length; i++) {
        merged.push({
          ...daysArray[i],
          ...this.allDaysInWeeks[moment(daysArray[i].dayISODate).format('YYYY-MM-DD')],
        });
      }

      const firstDayName = merged[0].dayName.toLowerCase();
      switch (
        firstDayName // Then we add empty items to the beginning of the array
      ) {
        case 'monday':
          merged.unshift({ dayISODate: this.getPastISO(1) });
          break;
        case 'tuesday':
          merged.unshift({ dayISODate: this.getPastISO(1) }, { dayISODate: this.getPastISO(2) });
          break;
        case 'wednesday':
          merged.unshift(
            { dayISODate: this.getPastISO(month, 3) },
            { dayISODate: this.getPastISO(month, 2) },
            { dayISODate: this.getPastISO(month, 1) }
          );
          break;
        case 'thursday':
          merged.unshift(
            { dayISODate: this.getPastISO(month, 4) },
            { dayISODate: this.getPastISO(month, 3) },
            { dayISODate: this.getPastISO(month, 2) },
            { dayISODate: this.getPastISO(month, 1) }
          );
          break;
        case 'friday':
          merged.unshift(
            { dayISODate: this.getPastISO(month, 5) },
            { dayISODate: this.getPastISO(month, 4) },
            { dayISODate: this.getPastISO(month, 3) },
            { dayISODate: this.getPastISO(month, 2) },
            { dayISODate: this.getPastISO(month, 1) }
          );
          break;
        case 'saturday':
          merged.unshift(
            { dayISODate: this.getPastISO(month, 6) },
            { dayISODate: this.getPastISO(month, 5) },
            { dayISODate: this.getPastISO(month, 4) },
            { dayISODate: this.getPastISO(month, 3) },
            { dayISODate: this.getPastISO(month, 2) },
            { dayISODate: this.getPastISO(month, 1) }
          );
          break;
      }
      this.$emit('set-is-loading', false);
      return _.chunk(merged, 7);
    },
    isWorkoutCompleted(day) {
      return (
        day &&
        day.workouts &&
        day.workouts.length > 0 &&
        day.workouts[0].hasWorkout &&
        day.workouts[0].clientInput.completedAt &&
        day.workouts[0].clientInput.completedAt !== ''
      );
    },
    workoutProgress(day) {
      const totalWorkoutElements =
        day &&
        day.workouts &&
        day.workouts.length > 0 &&
        day.workouts[0].hasWorkout &&
        day.workouts[0].clientInput.workoutProgress &&
        day.workouts[0].clientInput.workoutProgress.totalWorkoutElements
          ? day.workouts[0].clientInput.workoutProgress.totalWorkoutElements
          : 0;
      const completedWorkoutElements =
        day &&
        day.workouts &&
        day.workouts.length > 0 &&
        day.workouts[0].hasWorkout &&
        day.workouts[0].clientInput.workoutProgress &&
        day.workouts[0].clientInput.workoutProgress.completedWorkoutElements
          ? day.workouts[0].clientInput.workoutProgress.completedWorkoutElements
          : 0;

      return this.isWorkoutCompleted(day)
        ? 100
        : totalWorkoutElements > 0
        ? (completedWorkoutElements / totalWorkoutElements) * 100
        : 0;
    },
    updated(e) {
      const index = e.item.index;
      this.currentMonth = this.monthOptions[index];
    },
  },
};
</script>

<template>
  <div class="calendar-container" :style="cssVars">
    <div class="month row">
      <div>{{ currentMonthName }}</div>
    </div>
    <div class="week-days-name row">
      <template v-for="(day, i) in weekDays">
        <div :key="i" :class="['col day', { isToday: isToday(day) }]">{{ day.charAt(0) }}</div>
      </template>
    </div>
    <carousel
      class="carousel"
      :autoplay="false"
      :nav="false"
      :center="true"
      :dots="false"
      :items="1"
      :margin="0"
      :autoWidth="false"
      @dragged="updated"
      @to="updated"
      ref="carousel"
      :startPosition="5"
    >
      <template v-for="month in monthOptions">
        <div :key="month" class="week-days-number row">
          <div class="weeks-row row" :key="i" v-for="(week, i) in getDaysInMonth(month)">
            <template v-for="day in week">
              <div :key="day.date" :class="['day']">
                <div class="doughnut-chart-wrapper">
                  <div class="doughnut-chart-data">
                    <div
                      :class="[
                        'chart-num-wrapper',
                        {
                          'workout-available':
                            day && day.workouts && day.workouts.length > 0 && day.workouts[0] && day.workouts[0].hasWorkout,
                        },
                        { isToday: day && day.dayName && isToday(day.dayName, day.dayISODate) },
                      ]"
                      :style="{ width: doughnutChartWidth, height: doughnutChartHeight }"
                    >
                      <p>{{ day.dayNum }}</p>
                      <div v-if="isWorkoutCompleted(day)" class="icon-check-wrapper">
                        <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-check'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M33 66c18.225 0 33-14.775 33-33S51.225 0 33 0 0 14.775 0 33s14.775 33 33 33z" fill="url(#paint0_linear)" fill-opacity=".6"/><path d="M17.368 33.55l4.466-5.321 8.645 7.255 15.748-18.116 5.243 4.56L35.803 39.95l-4.466 5.322L17.368 33.55z" fill="#fff"/><defs><linearGradient id="paint0_linear" x1="33" y1="0" x2="33" y2="66" gradientUnits="userSpaceOnUse"><stop stop-color="#55BCF5"/><stop offset="1" stop-color="#3E4FE2"/></linearGradient></defs></svg>
                      </div>
                    </div>
                    <DoughnutChart
                      @update-chart-dim="getDoughnutChartDim"
                      class="doughnut-chart"
                      :data="{
                        ...chartData,
                        datasets: [
                          {
                            ...chartData.datasets[0],
                            data: [workoutProgress(day), 100 - workoutProgress(day)],
                          },
                        ],
                      }"
                      :options="chartOpts"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </carousel>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-id-pattern
// stylelint-disable selector-pseudo-element-no-unknown

.calendar-container {
  background-color: rgba(black, 1);
  height: 35rem;
  position: relative;
  width: 100%;
  .row {
    margin-left: unset;
    margin-right: unset;
  }
  .month {
    align-items: center;
    color: rgba(white, 1);
    display: flex;
    font-size: 18px;
    justify-content: center;
    padding-bottom: 20px;
    padding-top: 20px;
    text-transform: capitalize;
  }
  .week-days-name {
    flex-wrap: nowrap;
    justify-content: space-between;
    left: 0;
    margin-left: 0;
    margin-right: 0;
    padding-left: 5%;
    padding-right: 4%;
    position: absolute;
    right: 0;
    width: 96%;
    .day {
      color: rgba(white, 0.5);
      font-size: 15px;
      max-width: 8%;
      padding-left: unset;
      padding-right: unset;
      text-align: center;
      &.isToday {
        color: white;
      }
    }
  }

  .carousel {
    height: 80%;
    ::v-deep .owl-carousel {
      height: 100%;
      width: 100%;
      .owl-stage-outer {
        height: 100%;
        .owl-stage {
          display: flex;
          height: 100%;
          justify-content: flex-start;
          padding: unset;
          .owl-item {
            height: 100%;
            transition: all 0.25s ease-in-out;
            width: 100% !important;
          }
        }
      }
    }
  }

  .week-days-number {
    display: flex;
    flex-direction: column;
    height: 80%;
    justify-content: space-between;
    margin-left: auto;
    margin-right: auto;
    margin-top: 12%;
    width: 97%;
    .weeks-row {
      align-items: center;
      display: flex;
      flex-wrap: nowrap;
      justify-content: flex-start;
      margin-left: auto;
      margin-right: auto;
      position: relative;
      width: 98%;
    }
    .day {
      align-items: center;
      color: rgba(white, 0.5);
      display: flex;
      font-size: 13px;
      font-weight: 300;
      height: 25px;
      justify-content: center;
      // max-width: 25px;
      padding-left: unset;
      padding-right: unset;
      position: relative;
      text-align: center;
      width: 14%;
      .doughnut-chart-wrapper {
        align-items: center;
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-around;
        transition: all 200ms;
        transition-timing-function: ease-out;
        width: 100%;
        .doughnut-chart-data {
          align-items: center;
          display: flex;
          height: 30px;
          justify-content: center;
          position: relative;
          width: 30px;
          .doughnut-chart {
            height: 30px;
            max-height: 30px;
            max-width: 30px;
            position: absolute;
            width: 100%;
            .chartjs-size-monitor {
              height: fit-content;
            }
            #doughnut-chart {
              height: 30px;
              width: 30px;
            }
          }
          .chart-num-wrapper {
            align-items: center;
            border-radius: 50%;
            display: flex;
            height: 30px !important;
            justify-content: center;
            position: relative;
            text-align: center;
            width: 30px !important;
            p {
              font-size: 13px !important;
              font-weight: 300;
              line-height: unset;
              margin-bottom: unset !important;
              margin-top: 2px;
            }
            &.isToday {
              background-color: #3f3f3f;
              p {
                color: #fff !important;
                font-family: $secondary-font-family !important;
                font-weight: bold !important;
              }
            }
            &.workout-available {
              background-color: #3f3f3f;
              border-radius: 50%;
              color: white;
            }
            .icon-check-wrapper {
              align-items: center;
              border: 2px solid #1c1c1c;
              border-radius: 100%;
              bottom: -20%;
              display: flex;
              height: 50%;
              justify-content: center;
              position: absolute;
              right: -10%;
              text-align: center;
              width: 50%;
              z-index: 1;
              .icon-check {
                background-color: var(--color);
                border-radius: 100%;
                height: 100%;
                width: 100%;
                path {
                  &:first-child {
                    background-color: var(--color);
                    fill: var(--color);
                    fill-opacity: 1;
                    stroke: var(--color);
                  }

                  opacity: 1 !important;
                }
              }
            }
          }
        }
        label {
          margin-top: 35%;
          white-space: nowrap;
        }
      }
    }
  }
}
</style>
